/**
 * Scannable warehouse codes (TelcoTrack → AutoCRM GoodsIssue).
 *
 * EAN-13 cannot carry Espo IDs. Labels use **Code128** or **QR** with versioned
 * path payloads (pipe `|` separators are also accepted).
 *
 * ## Primary (separated labels)
 *
 * | Kind | Code | Moves with goods? |
 * |------|------|-------------------|
 * | Material | `TT1M/{sapCode}` | Yes (on box/coil) — **no warehouse** in barcode |
 * | Position | `TT1P/{warehouseId}` or `TT1P/{warehouseId}/{positionCode}` | No (rack / warehouse location) |
 *
 * At work: warehouse (and optionally position) comes from **context** — selected
 * warehouse in the app, or scan position first — then scan material.
 *
 * ## Legacy (transition)
 *
 * ```
 * TT1/{warehouseId}/{sapCode}
 * ```
 *
 * Optional trailing `/{warehouseItemId}` still decodes (pin piece if an old
 * per-piece label is scanned) but encoders never emit that segment.
 *
 * Prefer `encodeMaterialScanCode` / `encodePositionScanCode` for new labels.
 * `encodeWarehouseScanCode` remains as the legacy combined encoder.
 */

/** Legacy combined product+warehouse prefix. */
export const WAREHOUSE_SCAN_PREFIX = 'TT1';
/** Material-only (SAP / QR on goods). */
export const WAREHOUSE_SCAN_PREFIX_MATERIAL = 'TT1M';
/** Position / warehouse location on rack (does not move with goods). */
export const WAREHOUSE_SCAN_PREFIX_POSITION = 'TT1P';

export type WarehouseScanKind = 'material' | 'position' | 'legacy';

/** Material-only payload — warehouse must come from request/app context. */
export type WarehouseMaterialScanPayload = {
  kind: 'material';
  sapCode: string;
  /**
   * Legacy only — accepted when an old per-piece material label is scanned.
   * Primary material encode never emits this.
   */
  warehouseItemId?: string | null;
};

/** Rack / warehouse location — does not identify goods. */
export type WarehousePositionScanPayload = {
  kind: 'position';
  warehouseId: string;
  /** null = warehouse-level location label (no rack cell). */
  positionCode: string | null;
};

/** Combined product+warehouse (legacy TT1). */
export type WarehouseLegacyScanPayload = {
  kind: 'legacy';
  warehouseId: string;
  sapCode: string;
  warehouseItemId?: string | null;
};

export type WarehouseScanDecoded =
  | WarehouseMaterialScanPayload
  | WarehousePositionScanPayload
  | WarehouseLegacyScanPayload;

/**
 * @deprecated Prefer {@link WarehouseLegacyScanPayload} / discriminated
 * {@link WarehouseScanDecoded}. Kept for callers that only handle combined TT1.
 */
export type WarehouseScanPayload = {
  warehouseId: string;
  sapCode: string;
  warehouseItemId?: string | null;
};

const PART = '[A-Za-z0-9._-]{1,64}';

const MATERIAL_PATH_RE = new RegExp(`^TT1M\\/(${PART})(?:\\/(${PART}))?$`, 'i');
const MATERIAL_PIPE_RE = new RegExp(`^TT1M\\|(${PART})(?:\\|(${PART}))?$`, 'i');

const POSITION_PATH_RE = new RegExp(`^TT1P\\/(${PART})(?:\\/(${PART}))?$`, 'i');
const POSITION_PIPE_RE = new RegExp(`^TT1P\\|(${PART})(?:\\|(${PART}))?$`, 'i');

const LEGACY_PATH_RE = new RegExp(
  `^TT1\\/(${PART})\\/(${PART})(?:\\/(${PART}))?$`,
  'i',
);
const LEGACY_PIPE_RE = new RegExp(
  `^TT1\\|(${PART})\\|(${PART})(?:\\|(${PART}))?$`,
  'i',
);

function normalizePart(value: string | null | undefined): string {
  return String(value ?? '').trim();
}

function normalizeRaw(raw: string | null | undefined): string {
  return String(raw ?? '')
    .trim()
    .replace(/\s+/g, '');
}

/**
 * Encode material-only label for Code128 / QR (`TT1M/{sapCode}`).
 * No warehouse in the barcode — supply warehouse via scan context / request.
 */
export function encodeMaterialScanCode(sapCode: string): string {
  const code = normalizePart(sapCode);
  if (!code) {
    throw new Error('sapCode is required for material scan code');
  }
  return `${WAREHOUSE_SCAN_PREFIX_MATERIAL}/${code}`;
}

/**
 * Encode position / warehouse location label (`TT1P/{warehouseId}` or
 * `TT1P/{warehouseId}/{positionCode}`).
 */
export function encodePositionScanCode(payload: {
  warehouseId: string;
  positionCode?: string | null;
}): string {
  const warehouseId = normalizePart(payload.warehouseId);
  if (!warehouseId) {
    throw new Error('warehouseId is required for position scan code');
  }
  const positionCode = normalizePart(payload.positionCode);
  if (positionCode) {
    return `${WAREHOUSE_SCAN_PREFIX_POSITION}/${warehouseId}/${positionCode}`;
  }
  return `${WAREHOUSE_SCAN_PREFIX_POSITION}/${warehouseId}`;
}

/**
 * Encode legacy combined product + warehouse label (`TT1/{warehouseId}/{sapCode}`).
 * `warehouseItemId` is ignored (piece selection happens after scan).
 * Prefer {@link encodeMaterialScanCode} + position/context for new labels.
 */
export function encodeWarehouseScanCode(payload: WarehouseScanPayload): string {
  const warehouseId = normalizePart(payload.warehouseId);
  const sapCode = normalizePart(payload.sapCode);
  if (!warehouseId || !sapCode) {
    throw new Error('warehouseId and sapCode are required for warehouse scan code');
  }
  return `${WAREHOUSE_SCAN_PREFIX}/${warehouseId}/${sapCode}`;
}

/** @deprecated Alias — use {@link encodeWarehouseScanCode}. */
export const encodeLegacyWarehouseScanCode = encodeWarehouseScanCode;

/**
 * Decode a scanned Code128 / QR / pasted TT1 / TT1M / TT1P payload.
 * Returns null when the string is not a TelcoTrack warehouse scan code.
 */
export function decodeWarehouseScanCode(
  raw: string | null | undefined,
): WarehouseScanDecoded | null {
  const text = normalizeRaw(raw);
  if (!text) return null;

  const materialPath = text.match(MATERIAL_PATH_RE);
  if (materialPath) {
    return {
      kind: 'material',
      sapCode: materialPath[1],
      warehouseItemId: materialPath[2] ?? null,
    };
  }
  const materialPipe = text.match(MATERIAL_PIPE_RE);
  if (materialPipe) {
    return {
      kind: 'material',
      sapCode: materialPipe[1],
      warehouseItemId: materialPipe[2] ?? null,
    };
  }

  const positionPath = text.match(POSITION_PATH_RE);
  if (positionPath) {
    return {
      kind: 'position',
      warehouseId: positionPath[1],
      positionCode: positionPath[2] ?? null,
    };
  }
  const positionPipe = text.match(POSITION_PIPE_RE);
  if (positionPipe) {
    return {
      kind: 'position',
      warehouseId: positionPipe[1],
      positionCode: positionPipe[2] ?? null,
    };
  }

  const legacyPath = text.match(LEGACY_PATH_RE);
  if (legacyPath) {
    return {
      kind: 'legacy',
      warehouseId: legacyPath[1],
      sapCode: legacyPath[2],
      warehouseItemId: legacyPath[3] ?? null,
    };
  }
  const legacyPipe = text.match(LEGACY_PIPE_RE);
  if (legacyPipe) {
    return {
      kind: 'legacy',
      warehouseId: legacyPipe[1],
      sapCode: legacyPipe[2],
      warehouseItemId: legacyPipe[3] ?? null,
    };
  }

  return null;
}

export function isWarehouseScanCode(raw: string | null | undefined): boolean {
  return decodeWarehouseScanCode(raw) != null;
}

export function isMaterialScanCode(raw: string | null | undefined): boolean {
  return decodeWarehouseScanCode(raw)?.kind === 'material';
}

export function isPositionScanCode(raw: string | null | undefined): boolean {
  return decodeWarehouseScanCode(raw)?.kind === 'position';
}

/**
 * Narrow decoded payload to legacy shape when callers still expect
 * `{ warehouseId, sapCode }` only (returns null for material/position).
 */
export function asLegacyWarehouseScanPayload(
  decoded: WarehouseScanDecoded | null | undefined,
): WarehouseScanPayload | null {
  if (!decoded || decoded.kind !== 'legacy') return null;
  return {
    warehouseId: decoded.warehouseId,
    sapCode: decoded.sapCode,
    warehouseItemId: decoded.warehouseItemId ?? null,
  };
}

/** Human-readable position label line, e.g. `Lovosice · A-01`. */
export function formatPositionLabelDisplay(
  warehouseName: string,
  positionCode?: string | null,
): string {
  const wh = normalizePart(warehouseName);
  const pos = normalizePart(positionCode);
  if (wh && pos) return `${wh} · ${pos}`;
  return wh || pos || '';
}
